import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Layout } from "@/components/Layout";
import Home from "@/pages/Home";
import Listing from "@/pages/Listing";
import SearchPage from "@/pages/SearchPage";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import AIOrder from "@/pages/AIOrder";
import Checkout from "@/pages/Checkout";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Hotels from "@/pages/Hotels";
import HotelDetail from "@/pages/HotelDetail";
import JamaicaMade from "@/pages/JamaicaMade";
import ShopPlus from "@/pages/ShopPlus";
import GetApp from "@/pages/GetApp";
import MerchantStore from "@/pages/MerchantStore";
import Deals from "@/pages/Deals";
import Services from "@/pages/Services";
import Support from "@/pages/Support";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import BiometricSetup from "@/pages/BiometricSetup";
import ResetPassword from "@/pages/ResetPassword";
import BuildCalculator from "@/pages/BuildCalculator";
import HardwareCalculator from "@/pages/HardwareCalculator";
import PalletLoader from "@/pages/PalletLoader";
import PhotoUpload from "@/pages/PhotoUpload";
import MaterialsCatalogue from "@/pages/MaterialsCatalogue";
import Automotive, { DealerApply } from "@/pages/Automotive";
import DealerPortal from "@/pages/DealerPortal";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import Seller from "@/pages/Seller";
import Admin from "@/pages/Admin";
import Driver from "@/pages/Driver";
import Haulage from "@/pages/Haulage";
import DeliveryHub from "@/pages/DeliveryHub";
import Export from "@/pages/Export";
import Snap from "@/pages/Snap";
import Books from "@/pages/Books";
import HaulageRequest from "@/pages/HaulageRequest";
import Compare from "@/pages/Compare";
import Careers from "@/pages/Careers";
import Community from "@/pages/Community";
import Track from "@/pages/Track";
import Legal from "@/pages/Legal";
import CourierProfile from "@/pages/CourierProfile";
import Quote from "@/pages/Quote";
import PayNow from "@/pages/PayNow";
import FygaroReturn from "@/pages/FygaroReturn";
import FoodHome from "@/pages/food/FoodHome";
import Restaurant from "@/pages/food/Restaurant";
import RestaurantConsole from "@/pages/RestaurantConsole";
import VerifyOrder from "@/pages/VerifyOrder";
import SupplierPortal from "@/pages/SupplierPortal";
import HardwareHub from "@/pages/hardware/HardwareHub";
import PaintHub from "@/pages/PaintHub";
import GroceryHub from "@/pages/GroceryHub";
import HardwareCategory from "@/pages/hardware/HardwareCategory";
import CubaCollection from "@/pages/CubaCollection";
import { SupplierQuotes } from "@/components/hardware/Rfq";
import TravelHome from "@/pages/travel/TravelHome";
import TravelSearch from "@/pages/travel/TravelSearch";
import TravelProperty from "@/pages/travel/TravelProperty";
import TravelBook from "@/pages/travel/TravelBook";
import TravelTrips from "@/pages/travel/TravelTrips";
import TravelHost from "@/pages/travel/TravelHost";
import { CompareBar } from "@/components/CompareBar";
import AutoTranslate from "@/components/AutoTranslate";
import { ReauthModal } from "@/components/ReauthModal";
import { BiometricNudge } from "@/components/BiometricSignIn";
import { DealProvider } from "@/context/DealContext";
import { FxLoader } from "@/components/Price";

// Single persistent layout route: Header/Footer/CartDrawer stay mounted across navigation (no remount flicker, no refetch).
function Shell() { return <Layout><Outlet /></Layout>; }

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <DealProvider>
      <ScrollToTop />
      <AutoTranslate />
      <ReauthModal />
      <BiometricNudge />
      <FxLoader>
      <Routes>
        <Route element={<Shell />}>
        <Route path="/" element={<Home />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/pallet-loader" element={<PalletLoader />} />
        <Route path="/photo-upload/:token" element={<PhotoUpload />} />
        <Route path="/materials/:cat" element={<MaterialsCatalogue />} />
        <Route path="/automotive" element={<Automotive />} />
        <Route path="/automotive/brand/:slug" element={<Automotive />} />
        <Route path="/automotive/dealers/apply" element={<DealerApply />} />
        <Route path="/automotive/dealer" element={<DealerPortal />} />
        <Route path="/pallet-loader/estimator" element={<HardwareCalculator />} />
        <Route path="/pallet-loader/walls" element={<BuildCalculator />} />
        <Route path="/calculator" element={<Navigate to="/pallet-loader" replace />} />
        <Route path="/calculator/walls" element={<Navigate to="/pallet-loader/walls" replace />} />
        <Route path="/category/:slug" element={<Listing />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/ai-order" element={<AIOrder />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/payment/fygaro-return" element={<FygaroReturn />} />
        <Route path="/hotels" element={<Hotels />} />
        <Route path="/stays" element={<Hotels />} />
        <Route path="/hotels/:slug" element={<HotelDetail />} />
        <Route path="/jamaica-made" element={<JamaicaMade />} />
        <Route path="/shop-plus" element={<ShopPlus />} />
        <Route path="/get-app" element={<GetApp />} />
        <Route path="/merchant/:slug" element={<MerchantStore />} />
        <Route path="/deals" element={<Deals />} />
        <Route path="/services" element={<Services />} />
        <Route path="/support" element={<Support />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/account" element={<Account />} />
        <Route path="/account/biometrics" element={<BiometricSetup />} />
        <Route path="/seller" element={<Seller />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/driver" element={<Driver />} />
        <Route path="/delivery" element={<DeliveryHub />} />
        <Route path="/export" element={<Export />} />
        <Route path="/snap" element={<Snap />} />
        <Route path="/haulage" element={<Haulage />} />
        <Route path="/haulage/request/:code" element={<HaulageRequest />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/community" element={<Community />} />
        <Route path="/track" element={<Track />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/delivery-network" element={<Navigate to="/delivery" replace />} />
        <Route path="/courier/:uid" element={<CourierProfile />} />
        <Route path="/quote" element={<Quote />} />
        <Route path="/pay" element={<PayNow />} />
        <Route path="/food" element={<FoodHome />} />
        <Route path="/food/:slug" element={<Restaurant />} />
        <Route path="/restaurant" element={<RestaurantConsole />} />
        <Route path="/restaurant/apply" element={<RestaurantConsole />} />
        <Route path="/verify-order/:token" element={<VerifyOrder />} />
        <Route path="/supplier" element={<SupplierPortal />} />
        <Route path="/hardware" element={<HardwareHub />} />
        <Route path="/paint" element={<PaintHub />} />
        <Route path="/grocery" element={<GroceryHub />} />
        <Route path="/books" element={<Books />} />
        <Route path="/cuba" element={<CubaCollection />} />
        <Route path="/hardware/c/:slug" element={<HardwareCategory />} />
        <Route path="/hardware/quotes" element={<div className="max-w-5xl mx-auto px-4 py-10"><h1 className="font-clash font-bold text-3xl mb-6">Supplier quotations</h1><SupplierQuotes /></div>} />
        <Route path="/kfc" element={<Navigate to="/food/kfc" replace />} />
        <Route path="/travel" element={<TravelHome />} />
        <Route path="/travel/search" element={<TravelSearch />} />
        <Route path="/travel/property/:id" element={<TravelProperty />} />
        <Route path="/travel/book/:quoteId" element={<TravelBook />} />
        <Route path="/travel/trips" element={<TravelTrips />} />
        <Route path="/travel/trips/:ref" element={<TravelTrips />} />
        <Route path="/travel/host" element={<TravelHost />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      </FxLoader>
      <CompareBar />
      </DealProvider>
    </BrowserRouter>
  );
}
